import { type FC, type ReactNode, useCallback, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { viewMore, viewLess } from '../../finding/sbMessages';
import { PageLayout } from 'dibs-elements/exports/PageLayout';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { Button } from 'dibs-elements/exports/Button';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { RESULTS_FIRST_ROW_COUNT } from '../../constants/sbConstants';
import { useViewMore } from '../../finding/hooks/viewMore/useViewMore';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { FullPageWidth } from '../../components/global/FullPageWidth/FullPageWidth';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import classnames from 'classnames';

import styles from './SharedRelatedItemsWrapper.scss';

import { type SharedRelatedItemsWrapper_user$key } from './__generated__/SharedRelatedItemsWrapper_user.graphql';
import { type SharedRelatedItemsWrapper_item$key } from './__generated__/SharedRelatedItemsWrapper_item.graphql';

const SMALL_TILE_TYPE = 'imageSmall';
const X_SMALL_TILE_TYPE = 'imageXSmall';

const userFragment = graphql`
    fragment SharedRelatedItemsWrapper_user on User {
        serviceId
    }
`;

const itemsFragment = graphql`
    fragment SharedRelatedItemsWrapper_item on Item @relay(plural: true) {
        ...FavoritesProvider_item
    }
`;

export type ChildrenProps = {
    currency: string;
    favorites: FavoritesProviderChildrenProps;
    imageSize: typeof SMALL_TILE_TYPE | typeof X_SMALL_TILE_TYPE;
    itemClassName: (index: number) => {
        wrapperClassName?: string | null;
        favoritesClassName?: string | null;
    };
};

type Props = {
    user: SharedRelatedItemsWrapper_user$key;
    children: (props: ChildrenProps) => ReactNode;
    header: ReactNode;
    items: SharedRelatedItemsWrapper_item$key;
    isMobile: boolean;
    isClient: boolean;
    isDark?: boolean;
    showSwiper?: boolean;
    initialCount?: number;
    onItemsImpression: ({
        startIndex,
        itemsLengthToShow,
    }: {
        startIndex: number;
        itemsLengthToShow: number;
    }) => void;
    numberOfItemsToShow?: number;
};

export const SharedRelatedItemsWrapper: FC<Props> = ({
    user: userRef,
    children,
    header,
    items: itemsRef,
    isMobile,
    isClient,
    isDark,
    showSwiper = false,
    initialCount = RESULTS_FIRST_ROW_COUNT * 2,
    onItemsImpression = () => {},
}) => {
    const user = useFragment(userFragment, userRef);
    const items = useFragment(itemsFragment, itemsRef);

    const { isItemTileRedesignTestVariant } = useServerVarsContext();
    const totalCount = items?.length;

    const [isViewMoreImpressionFired, setIsViewMoreImpressionFired] = useState(false);
    const {
        viewMoreEl,
        dispatch: toggleViewMore,
        visibleCount,
    } = useViewMore<HTMLButtonElement>({
        totalCount,
        revealCount: totalCount - initialCount,
        initialCount,
    });

    const itemClassName = useCallback(
        (index: number) => {
            if (showSwiper) {
                return {
                    favoritesClassName: styles.swiperItemFavorites,
                };
            } else {
                return {
                    customClassName: classnames({
                        [styles.disablePadding]: isDark,
                        [styles.respItemGrid]: !isMobile,
                        [styles.mobileItemGrid]: isMobile,
                        [styles.hideTile]: index >= visibleCount,
                    }),
                };
            }
        },
        [showSwiper, visibleCount, isMobile, isDark]
    );

    const trackViewMoreItemsImpression = (): void => {
        if (!isViewMoreImpressionFired) {
            onItemsImpression({
                startIndex: initialCount,
                itemsLengthToShow: totalCount - initialCount,
            });
            setIsViewMoreImpressionFired(true);
        }
    };

    const imageSize = isMobile ? X_SMALL_TILE_TYPE : SMALL_TILE_TYPE;

    return (
        <>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        onItemsImpression({
                            startIndex: 0,
                            itemsLengthToShow: visibleCount,
                        });
                    }
                }}
            />
            <FavoritesProvider
                disable={!isClient}
                items={items}
                location={locations.SEARCH_BROWSE}
                userId={user?.serviceId}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <CurrencyProvider
                        render={({ currency }: { currency: ChildrenProps['currency'] }) => (
                            <QuickViewProvider>
                                <PageLayout setViewportBackground={isDark}>
                                    <div
                                        className={classnames(styles.container, {
                                            [styles.topSpacer]: !isDark && !showSwiper,
                                        })}
                                    >
                                        <HeadingLevel>
                                            {Heading => (
                                                <Heading className={styles.header}>
                                                    {header}
                                                </Heading>
                                            )}
                                        </HeadingLevel>
                                        {showSwiper ? (
                                            <FullPageWidth>
                                                <Swiper itemsPerPage={2.5} itemsTopAlign>
                                                    {children({
                                                        currency,
                                                        favorites,
                                                        imageSize,
                                                        itemClassName,
                                                    })}
                                                </Swiper>
                                            </FullPageWidth>
                                        ) : (
                                            <div
                                                className={
                                                    isItemTileRedesignTestVariant
                                                        ? classnames(styles.gridContainer, {
                                                              [styles.limit4Items]:
                                                                  visibleCount < totalCount &&
                                                                  initialCount ===
                                                                      RESULTS_FIRST_ROW_COUNT,
                                                              [styles.limit8Items]:
                                                                  visibleCount < totalCount &&
                                                                  initialCount ===
                                                                      RESULTS_FIRST_ROW_COUNT * 2,
                                                          })
                                                        : styles.itemGrid
                                                }
                                            >
                                                {children({
                                                    currency,
                                                    favorites,
                                                    imageSize,
                                                    itemClassName,
                                                })}
                                            </div>
                                        )}
                                        {!showSwiper && totalCount > initialCount && (
                                            <div className={styles.viewMoreSection}>
                                                <Button
                                                    className={styles.viewMoreButton}
                                                    buttonRef={viewMoreEl}
                                                    size="medium"
                                                    type="secondary"
                                                    onClick={() => {
                                                        trackViewMoreItemsImpression();
                                                        toggleViewMore();
                                                    }}
                                                >
                                                    {visibleCount < totalCount
                                                        ? viewMore
                                                        : viewLess}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </PageLayout>
                            </QuickViewProvider>
                        )}
                    />
                )}
            </FavoritesProvider>
        </>
    );
};
